import React, { useState } from 'react';
import usePaginatedResource from '../../../hooks/usePaginatedResource';
import useResourceMutation from '../../../hooks/useResourceMutation';
import PageHeader from '../../../components/shared/PageHeader/PageHeader';
import DataTable, { Column } from '../../../components/shared/DataTable/DataTable';
import ConfirmDialog from '../../../components/shared/ConfirmDialog/ConfirmDialog';
import Button from '../../../components/shared/Button/Button';
import StatusBadge from '../../../components/shared/StatusBadge/StatusBadge';
import {
  approveWithdrawal,
  rejectWithdrawal,
} from '../../../services/withdrawals';
import type { WithdrawalRequest } from '../../../types/api';

type PendingAction =
  | { type: 'approve' | 'reject'; req: WithdrawalRequest }
  | null;

const Withdrawals: React.FC = () => {
  const { data: wds, loading, refetch } = usePaginatedResource<WithdrawalRequest>('/finance/withdrawals/');
  const [pending, setPending] = useState<PendingAction>(null);

  const approveM = useResourceMutation((id: number) => approveWithdrawal(id), { successMessage: 'Approved' });
  const rejectM = useResourceMutation((id: number) => rejectWithdrawal(id), { successMessage: 'Rejected' });

  const handleConfirm = async () => {
    if (!pending) return;
    try {
      if (pending.type === 'approve') await approveM.run(pending.req.id);
      else await rejectM.run(pending.req.id);
      setPending(null);
      refetch();
    } catch { /* toast */ }
  };

  const columns: Column<WithdrawalRequest>[] = [
    { key: 'wallet', header: 'Wallet', render: (w) => `#${w.wallet}` },
    { key: 'amount', header: 'Amount', align: 'right',
      render: (w) => `KES ${Number(w.amount).toLocaleString()}` },
    { key: 'phone_number', header: 'Phone' },
    { key: 'status', header: 'Status', filterable: true, filterOptions: [
      { value: 'pending', label: 'Pending' },
      { value: 'submitted', label: 'Submitted' },
      { value: 'processed', label: 'Processed' },
      { value: 'failed', label: 'Failed' },
      { value: 'rejected', label: 'Rejected' },
    ], render: (w) => <StatusBadge variant="withdrawal" status={w.status} /> },
    { key: 'failure_reason', header: 'Failure',
      render: (w) => (w.status === 'failed' && w.failure_reason)
        ? <span className="text-xs text-rose-700">{w.failure_reason}</span>
        : '—' },
    { key: 'created_at', header: 'Requested', sortable: true,
      render: (w) => new Date(w.created_at).toLocaleString() },
    { key: 'processed_at', header: 'Processed',
      render: (w) => w.processed_at ? new Date(w.processed_at).toLocaleString() : '—' },
  ];

  const actions = (w: WithdrawalRequest) => (
    <div className="inline-flex items-center gap-2">
      {w.status === 'pending' && (
        <>
          <Button size="sm" onClick={() => setPending({ type: 'approve', req: w })}>
            Approve & send
          </Button>
          <Button variant="danger" size="sm" onClick={() => setPending({ type: 'reject', req: w })}>
            Reject
          </Button>
        </>
      )}
      {w.status === 'submitted' && (
        <span className="text-xs italic text-slate-500">B2C in flight</span>
      )}
    </div>
  );

  const confirmTitle =
    pending?.type === 'approve' ? 'Approve & send withdrawal?' :
    'Reject withdrawal?';
  const confirmLabel =
    pending?.type === 'approve' ? 'Approve & send' : 'Reject';
  const confirmVariant: 'primary' | 'danger' = pending?.type === 'reject' ? 'danger' : 'primary';

  return (
    <>
      <PageHeader title="Withdrawals" description="Owner withdrawal requests across all statuses." />
      <DataTable<WithdrawalRequest>
        columns={columns} data={wds} rowKey="id"
        loading={loading}
        emptyTitle="No withdrawal requests"
        emptyDescription="Approved withdrawals from partners will show up here."
        actions={actions}
      />

      <ConfirmDialog
        isOpen={!!pending}
        title={confirmTitle}
        message={pending
          ? `${confirmLabel} KES ${Number(pending.req.amount).toLocaleString()} for wallet #${pending.req.wallet}?`
          : ''
        }
        confirmLabel={confirmLabel}
        variant={confirmVariant}
        loading={approveM.loading || rejectM.loading}
        onConfirm={handleConfirm}
        onCancel={() => setPending(null)}
      />
    </>
  );
};

export default Withdrawals;
